import { motion } from "framer-motion";
import { Flame, Heart, Users, MessageCircle } from "lucide-react";
import { IMG, SITE } from "@/data/site";

export default function Campfire() {
  const waHref = `https://wa.me/${SITE.phoneE164}?text=${encodeURIComponent(
    "Hi Peak Adventures Malnad, I'd like to book a bonfire night stay."
  )}`;

  return (
    <section
      id="bonfire"
      className="relative bg-[var(--pam-forest)] text-white overflow-hidden"
      style={{ padding: "5% 0" }}
      data-testid="campfire-section"
    >
      <div className="container-pam">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[var(--pam-gold)]">
            <Flame className="w-4 h-4" />
            <span className="overline">Bonfire nights & camps</span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            An authentic Malnad
            <span className="italic text-[var(--pam-gold)]">
              {" "}campfire evening.
            </span>
          </motion.h2>
          <p className="mt-6 text-white/75 leading-relaxed max-w-2xl">
            When the sun dips behind the coffee hills and mist rolls in, we
            light the fire. Log-wood stacked tall, filter coffee brewing on the
            side, roasted corn on the coals, and stories that only Malnad hills
            know. Our bonfire is the heart of the estate — where guests slow
            down, laugh loud, and remember why they came.
          </p>
        </div>

        {/* Highlight badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl"
        >
          {[
            {
              icon: <Heart className="w-4 h-4" />,
              t: "Best for couples",
              d: "Private pits, warm blankets, a sky full of stars.",
            },
            {
              icon: <Users className="w-4 h-4" />,
              t: "Best for friends",
              d: "Group jams, roasted snacks, storytelling till late.",
            },
            {
              icon: <Flame className="w-4 h-4" />,
              t: "Best for families",
              d: "Kid-safe fires, folk songs and warm evenings together.",
            },
          ].map((h) => (
            <div
              key={h.t}
              className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-5"
              data-testid={`bonfire-highlight-${h.t.split(" ").pop().toLowerCase()}`}
            >
              <div className="inline-flex items-center gap-2 text-[var(--pam-gold)]">
                {h.icon}
                <span className="overline">{h.t}</span>
              </div>
              <p className="mt-3 text-sm text-white/75">{h.d}</p>
            </div>
          ))}
        </motion.div>

        {/* Bonfire cinematic video */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.2, 0.9, 0.2, 1] }}
          className="mt-14 rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10 relative bonfire-video-wrap"
          data-testid="bonfire-video-wrapper"
        >
          <video
            src="/bonfire-video.mp4"
            poster={IMG.campfire}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            decoding="async"
            className="w-full h-full object-cover"
            style={{
              objectPosition: "center",
              willChange: "transform",
              backfaceVisibility: "hidden",
              transform: "translateZ(0)",
            }}
            aria-label="Bonfire night at Peak Adventures Malnad partner campsite"
            data-testid="bonfire-video"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0A1612]/60 via-transparent to-[#0A1612]/25" />
        </motion.div>

        <div className="mt-12 flex flex-wrap items-center gap-4">
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold rounded-full px-7 py-3.5 text-sm font-medium tracking-wide flex items-center gap-2"
            data-testid="bonfire-book-btn"
          >
            <MessageCircle className="w-4 h-4" />
            Reserve a bonfire night
          </a>
        </div>
      </div>
    </section>
  );
}
